import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCheck, FaDownload, FaMapMarkerAlt, FaPhone, FaTruck } from "react-icons/fa";
import CommonService from "../../services/common.service";
import { API_URLS, resolvePublicFileUrl } from "../../constants/api-urls";
import { parseCmRfqIdParam, unwrapApiData } from "./cm/cmProductionPaths";
import "../../assets/styles/cm_product_tracking.scss";

function getObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

function safeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtWhen(d, { expected = false } = {}) {
  if (!d) return expected ? "Date TBC (expected)" : "—";
  const dateStr = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const timeStr = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return expected ? `${dateStr} (expected)` : `${dateStr}, ${timeStr}`;
}

/**
 * Derive headline, progress, and timeline from CM delivery API (DB-backed where available).
 * @param {{ sellerView?: boolean }} opts — seller workspace uses slightly different copy when delivery is accepted.
 */
function deriveTrackingView(detail, rfqId, opts = {}) {
  const sellerView = !!opts.sellerView;
  const snap = detail?.fulfillment || {};
  const grn = detail?.grn || {};
  const po = detail?.purchaseOrder || {};
  const acceptedAt = safeDate(detail?.rfq?.buyerDeliveryAcceptedAt);
  const grnDate = safeDate(grn.grnDate);
  const deliveryDate = safeDate(grn.deliveryDate);
  const expectedPo = safeDate(po?.expectedDeliveryDate);

  const manifestNo = String(snap.shippingManifest?.manifestNumber || "").trim();
  const ewb = String(snap.ewayBill?.ewbNumber || "").trim();
  const transport = String(grn.transportCompany || "").trim() || "Logistics partner";
  const carrierShort = transport.split(/\s+/)[0] || "SHP";
  const trackingNumber =
    manifestNo ? `${carrierShort}-${manifestNo}`.replace(/\s+/g, "") : ewb ? `EWB-${ewb}` : `CM-RFQ-${rfqId}`;

  const grnNum = String(grn.grnNumber || snap.grn?.grnNumber || "").trim();
  const grnStatus = String(grn.status || snap.grn?.status || "").toLowerCase();

  let progressPct = 18;
  let headlineStatus = "Preparing shipment";
  let currentLocation = "Awaiting dispatch details";
  let eta = expectedPo;

  if (acceptedAt) {
    progressPct = 100;
    headlineStatus = "Delivered";
    currentLocation = sellerView
      ? "Buyer confirmed receipt (delivery accepted)"
      : "Confirmed at your facility (delivery accepted)";
    eta = acceptedAt;
  } else if (grnNum) {
    if (grnStatus.includes("approved") || grnStatus.includes("complete")) {
      progressPct = 95;
      headlineStatus = "Received — processing GRN";
      currentLocation = grn.transportCompany ? `${transport} · destination` : "Destination facility";
    } else {
      progressPct = 82;
      headlineStatus = "Arrived — goods receipt";
      currentLocation = grn.transportCompany || "Receiving dock";
    }
    eta = deliveryDate || grnDate || expectedPo;
  } else if (manifestNo) {
    progressPct = 58;
    headlineStatus = "In transit";
    currentLocation = "International hub / in transit (see manifest)";
    eta = expectedPo;
  } else if (ewb) {
    progressPct = 38;
    headlineStatus = "Dispatched";
    currentLocation = "Road / air leg (e-way generated)";
    eta = expectedPo;
  }

  const origin = "Origin facility";
  const hub = transport.length > 24 ? "Transit hub" : `${transport} hub`;

  const steps = [
    {
      key: "pickup",
      title: "Picked up from manufacturer",
      place: origin,
      when: grnDate || acceptedAt,
      expected: !grnDate && !acceptedAt,
    },
    {
      key: "origin",
      title: "Processed at origin facility",
      place: manifestNo ? `Linked manifest ${manifestNo}` : "Sorting / consolidation",
      when: grnDate ? new Date(grnDate.getTime() + 5 * 3600000) : null,
      expected: !grnDate,
    },
    {
      key: "depart",
      title: "Departed / in network",
      place: ewb ? `E-way ${ewb}` : hub,
      when: deliveryDate ? new Date(deliveryDate.getTime() - 2 * 86400000) : null,
      expected: !deliveryDate && !grnDate,
    },
    {
      key: "transit",
      title: "In transit — current movement",
      place: currentLocation,
      when: deliveryDate || grnDate,
      expected: false,
    },
    {
      key: "dest",
      title: "Arrival at destination region",
      place: "Destination country / city",
      when: deliveryDate || expectedPo,
      expected: !deliveryDate,
    },
    {
      key: "grn",
      title: "Goods receipt (GRN)",
      place: grnNum ? `GRN ${grnNum}` : "Receiving warehouse",
      when: grnDate || deliveryDate,
      expected: !grnNum,
    },
    {
      key: "delivered",
      title: "Delivered — buyer confirmation",
      place: "Final destination",
      when: acceptedAt || deliveryDate,
      expected: !acceptedAt,
    },
  ];

  let currentIdx = 0;
  if (acceptedAt) currentIdx = 6;
  else if (grnNum) currentIdx = 5;
  else if (manifestNo) currentIdx = 3;
  else if (ewb) currentIdx = 2;
  else currentIdx = 1;

  const events = steps.map((s, i) => {
    let status = "upcoming";
    if (i < currentIdx) status = "done";
    else if (i === currentIdx) status = "current";
    const whenDate = s.when && !s.expected ? s.when : null;
    let whenLabel = "—";
    if (whenDate) whenLabel = fmtWhen(whenDate);
    else if (status === "upcoming" && expectedPo) whenLabel = fmtWhen(expectedPo, { expected: true });
    else if (status === "done") whenLabel = "Recorded in platform";
    return {
      ...s,
      status,
      whenLabel,
    };
  });

  const weightLabel = (() => {
    const rfqPayload = getObj(detail?.rfq?.payloadJson);
    const logistics = getObj(rfqPayload.logistics);
    const w = logistics.totalWeightKg ?? logistics.weightKg ?? logistics.weight;
    if (w != null && String(w).trim() !== "") return `${String(w).trim()} kg (from RFQ)`;
    return "—";
  })();

  const qty = (() => {
    const rfqPayload = getObj(detail?.rfq?.payloadJson);
    const project = getObj(rfqPayload.project);
    const q = project?.quantity ?? project?.orderQuantity ?? rfqPayload?.quantity;
    if (q == null || q === "") return "—";
    const n = Number(q);
    return Number.isFinite(n) ? `${n.toLocaleString()} units` : String(q);
  })();

  return {
    trackingNumber,
    headlineStatus,
    currentLocation,
    progressPct,
    eta,
    events,
    transport,
    weightLabel,
    qty,
    manifestNo,
    grnNum,
  };
}

export default function BuyerCmProductTrackingPage() {
  const location = useLocation();
  const isSellerView = /\/seller\/cm-rfqs\//.test(location.pathname);
  const { rfqId: rfqIdParam } = useParams();
  const rfqId = parseCmRfqIdParam(rfqIdParam) || rfqIdParam;
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = isSellerView
        ? API_URLS.CONTRACT_MANUFACTURER.SELLER_DELIVERY_TRACKING_GET(rfqId)
        : API_URLS.CONTRACT_MANUFACTURER.BUYER_DELIVERY_CONFIRMATION_GET(rfqId);
      const res = await CommonService.get(path);
      const d = unwrapApiData(res);
      setDetail(d);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [rfqId, isSellerView]);

  useEffect(() => {
    load();
  }, [load]);

  const pp = detail?.production;
  const snap = detail?.fulfillment || {};
  const deliveryAccepted = !!detail?.rfq?.buyerDeliveryAcceptedAt;
  const batchLabel = snap.orderLabel || (detail?.purchaseOrder?.poNumber ? `PO ${detail.purchaseOrder.poNumber}` : `RFQ #${rfqId}`);

  const view = useMemo(
    () => (detail ? deriveTrackingView(detail, rfqId, { sellerView: isSellerView }) : null),
    [detail, rfqId, isSellerView]
  );

  const grnDocs = Array.isArray(detail?.grnDocuments) ? detail.grnDocuments : [];

  function exportTracking() {
    if (!view || !detail) return;
    const lines = [
      `Product tracking — ${batchLabel}`,
      `Tracking number: ${view.trackingNumber}`,
      `Status: ${view.headlineStatus}`,
      `Current location: ${view.currentLocation}`,
      `Carrier / transport: ${view.transport}`,
      `Manifest: ${view.manifestNo || "—"}`,
      `GRN: ${view.grnNum || "—"}`,
      "",
      "Timeline:",
      ...view.events.map((e) => `- [${e.status}] ${e.title} @ ${e.place} — ${e.whenLabel}`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `tracking-${rfqId}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (loading) {
    return (
      <div className="cm-tracking-page container py-4">
        <p className="text-muted">Loading tracking…</p>
      </div>
    );
  }

  if (!pp?.exists) {
    return (
      <Navigate to={isSellerView ? "/seller/cm-rfqs" : "/dashboard/buyer/contract-manufacturers/quotes"} replace />
    );
  }

  if (!deliveryAccepted) {
    return (
      <Navigate
        to={
          isSellerView
            ? "/seller/cm-rfqs"
            : `/dashboard/buyer/contract-manufacturers/production/${rfqId}/delivery-confirmation`
        }
        replace
      />
    );
  }

  if (!view) return null;

  const etaLabel = view.eta ? view.eta.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

  return (
    <div className="cm-tracking-page container py-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          {isSellerView ? (
            <>
              <li className="breadcrumb-item">
                <Link to="/dashboard/seller">Seller home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/seller/cm-rfqs">CM RFQs received</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Product tracking
              </li>
            </>
          ) : (
            <>
              <li className="breadcrumb-item">
                <Link to="/dashboard/buyer">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/dashboard/buyer/contract-manufacturers/quotes">Contract manufacturing</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}`}>Production</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}/delivery-confirmation`}>
                  Delivery confirmation
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Product tracking
              </li>
            </>
          )}
        </ol>
      </nav>

      <header className="cm-tracking-page__header">
        <div>
          <h1 className="cm-tracking-page__title">
            {view.headlineStatus === "Delivered" ? "Shipment delivered" : "Shipment in transit"} — {batchLabel}
          </h1>
          <p className="cm-tracking-page__subtitle">
            Tracking number: <strong>{view.trackingNumber}</strong>
          </p>
        </div>
        <button type="button" className="btn cm-tracking-page__export" onClick={exportTracking}>
          <FaDownload className="me-2" aria-hidden />
          Export tracking info
        </button>
      </header>

      <section className="cm-tracking-page__summary">
        <div className="cm-tracking-page__summary-icon" aria-hidden>
          <FaTruck />
        </div>
        <div className="cm-tracking-page__summary-main">
          <div className="cm-tracking-page__summary-row">
            <span className="cm-tracking-page__summary-status">{view.headlineStatus}</span>
            <span className="cm-tracking-page__summary-eta">Est. delivery: {etaLabel}</span>
          </div>
          <p className="cm-tracking-page__summary-loc mb-2">
            Current location: <strong>{view.currentLocation}</strong>
          </p>
          <div className="cm-tracking-page__progress">
            <div className="cm-tracking-page__progress-fill" style={{ width: `${view.progressPct}%` }} />
          </div>
        </div>
      </section>

      <div className="row g-4">
        <div className="col-lg-8">
          <section className="cm-tracking-page__card">
            <h2 className="cm-tracking-page__card-title">Tracking history</h2>
            <ul className="cm-tracking-page__timeline">
              {view.events.map((ev) => (
                <li
                  key={ev.key}
                  className={`cm-tracking-page__timeline-item cm-tracking-page__timeline-item--${ev.status}`}
                >
                  <div className="cm-tracking-page__timeline-marker">
                    {ev.status === "done" ? (
                      <FaCheck aria-hidden />
                    ) : ev.status === "current" ? (
                      <FaMapMarkerAlt aria-hidden />
                    ) : (
                      <span className="cm-tracking-page__timeline-dot" />
                    )}
                  </div>
                  <div className="cm-tracking-page__timeline-body">
                    <h3 className="cm-tracking-page__timeline-title">{ev.title}</h3>
                    <p className="cm-tracking-page__timeline-place">{ev.place}</p>
                    <p className="cm-tracking-page__timeline-when">{ev.whenLabel}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="cm-tracking-page__footnote small text-muted mb-0">
              Timeline combines your GRN, manifest, and e-way data from the platform where available; remaining steps
              show expected milestones until confirmed in the system.
            </p>
          </section>
        </div>

        <div className="col-lg-4">
          <div className="cm-tracking-page__side-card">
            <h2 className="cm-tracking-page__card-title">Shipment details</h2>
            <dl className="cm-tracking-page__dl">
              <dt>Carrier</dt>
              <dd>{view.transport}</dd>
              <dt>Service type</dt>
              <dd>Standard B2B freight</dd>
              <dt>Weight</dt>
              <dd>{view.weightLabel}</dd>
              <dt>Order quantity</dt>
              <dd>{view.qty}</dd>
            </dl>
          </div>

          <div className="cm-tracking-page__side-card">
            <h2 className="cm-tracking-page__card-title">Contact</h2>
            <p className="cm-tracking-page__contact-line">
              <FaPhone className="me-2 text-muted" aria-hidden />
              <span>Carrier support: use your logistics SPOC or manifest contact.</span>
            </p>
            <p className="small text-muted mb-3">Platform support for order issues:</p>
            <Link to="/contact" className="btn btn-outline-secondary w-100">
              Report issue
            </Link>
          </div>

          <div className="cm-tracking-page__side-card">
            <h2 className="cm-tracking-page__card-title">Documents</h2>
            <ul className="cm-tracking-page__doc-list list-unstyled mb-0">
              {grnDocs.length === 0 ? (
                <li className="text-muted small">No GRN documents on file. Check shipping manifests for BOL / invoice.</li>
              ) : (
                grnDocs.map((doc) => {
                  const href = resolvePublicFileUrl(doc.documentPath);
                  return (
                    <li key={doc.id}>
                      <a href={href} target="_blank" rel="noopener noreferrer" className="cm-tracking-page__doc-link">
                        <FaDownload className="me-2" aria-hidden />
                        {doc.documentName || doc.documentType || "Document"}
                      </a>
                    </li>
                  );
                })
              )}
              <li>
                <Link to="/shipping-manifest-list" className="cm-tracking-page__doc-link">
                  <FaDownload className="me-2" aria-hidden />
                  Shipping manifests
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {isSellerView ? (
          <Link to="/seller/cm-rfqs" className="btn btn-outline-secondary">
            Back to CM RFQs
          </Link>
        ) : (
          <>
            <Link
              to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}/delivery-confirmation`}
              className="btn btn-outline-secondary me-2"
            >
              Back to delivery confirmation
            </Link>
            <Link
              to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}`}
              className="btn btn-outline-secondary"
            >
              Production workspace
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
